import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styles from './faq.module.css';

/**
 * Renders one FAQ category: an icon + heading, then a Radix accordion of its
 * questions. Open state is controlled by the page so a URL hash can open a
 * specific question.
 */
const FaqAccordion = ({ category, icon: Icon, openItems, onOpenChange }) => {
  const navigate = useNavigate();

  const handleLink = (event, to) => {
    if (to.startsWith('mailto:')) return;
    event.preventDefault();
    navigate(to);
  };

  return (
    <section
      id={category.id}
      className={`${styles.category} ${styles.anchor}`}
      data-testid={`faq-category-${category.id}`}
    >
      <div className={styles.categoryHead}>
        <div className={styles.iconBox}>
          <Icon size={20} aria-hidden="true" />
        </div>
        <h2>{category.label}</h2>
      </div>

      <AccordionPrimitive.Root type="multiple" value={openItems} onValueChange={onOpenChange}>
        {category.questions.map((question) => (
          <AccordionPrimitive.Item
            key={question.id}
            value={question.id}
            id={question.id}
            className={`${styles.item} ${styles.anchor}`}
          >
            <AccordionPrimitive.Header>
              <AccordionPrimitive.Trigger
                className={styles.trigger}
                data-testid={`faq-trigger-${question.id}`}
              >
                <span>{question.q}</span>
                <ChevronDown size={20} className={styles.chevron} aria-hidden="true" />
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>

            <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <div className={styles.answer} data-testid={`faq-answer-${question.id}`}>
                {question.body.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}

                {question.bullets && (
                  <ul>
                    {question.bullets.map((bullet, index) => (
                      <li key={index}>{bullet}</li>
                    ))}
                  </ul>
                )}

                {question.links && (
                  <div className={styles.answerLinks}>
                    {question.links.map((related) => (
                      <a
                        key={related.to}
                        href={related.to}
                        className={styles.answerLink}
                        onClick={(event) => handleLink(event, related.to)}
                        data-testid={`faq-related-${related.to.replace(/\//g, '')}`}
                      >
                        {related.label}
                        <ArrowRight size={15} aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                )}

                {question.link && (
                  <a
                    href={question.link.to}
                    className={styles.answerLink}
                    onClick={(event) => handleLink(event, question.link.to)}
                    data-testid={`faq-link-${question.id}`}
                  >
                    {question.link.label}
                    <ArrowRight size={15} aria-hidden="true" />
                  </a>
                )}
              </div>
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Item>
        ))}
      </AccordionPrimitive.Root>
    </section>
  );
};

export default FaqAccordion;
